import React from 'react';
import { getUserById, updateUserItem } from '../../../../../../../helpers/user-helper';
import { defaultEditFormState } from '../helpers/settingsUsersFormConfig';

export const useSettingsUsersEditForm = ({ token, userId, currentUserId }) => {
    const [isLoading, setIsLoading] = React.useState(true);
    const [isSaving, setIsSaving] = React.useState(false);
    const [error, setError] = React.useState('');
    const [successMessage, setSuccessMessage] = React.useState('');
    const [form, setForm] = React.useState(defaultEditFormState);

    React.useEffect(() => {
        const loadUser = async () => {
            if (!token || !userId) {
                setError('No se pudo validar la sesión o el usuario a editar.');
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            setError('');

            const resp = await getUserById(token, userId);
            if (!resp?.success || !resp?.data) {
                setError(resp?.message || 'No se pudo cargar el usuario.');
                setIsLoading(false);
                return;
            }

            const user = resp.data;
            setForm({
                firstName: user.firstName || '',
                lastName: user.lastName || '',
                email: user.email || '',
                role: user.role || 'USER',
                password: '',
                active: Boolean(user.active),
            });
            setIsLoading(false);
        };

        loadUser();
    }, [token, userId]);

    const handleInputChange = React.useCallback((event) => {
        const { name, value } = event.target;
        setForm((previous) => ({
            ...previous,
            [name]: value,
        }));
    }, []);

    const handleStatusChange = React.useCallback((event) => {
        const nextValue = String(event.target.value || '').trim().toUpperCase();
        setForm((previous) => ({
            ...previous,
            active: nextValue === 'ACTIVE',
        }));
    }, []);

    const handleSubmit = React.useCallback(async (event) => {
        event.preventDefault();

        if (!token || !userId || isSaving) {
            return;
        }

        const payload = {
            firstName: String(form.firstName || '').trim(),
            lastName: String(form.lastName || '').trim(),
            email: String(form.email || '').trim().toLowerCase(),
            role: String(form.role || 'USER').trim().toUpperCase(),
            active: Boolean(form.active),
        };
        payload.name = `${payload.firstName} ${payload.lastName}`.trim();

        if (!payload.firstName || !payload.lastName || !payload.email) {
            setError('Completa nombre, apellido y correo electrónico.');
            setSuccessMessage('');
            return;
        }

        if (!payload.active && currentUserId === userId) {
            setError('No puedes desactivar tu propio usuario administrador.');
            setSuccessMessage('');
            return;
        }

        if (String(form.password || '').trim()) {
            payload.password = String(form.password || '').trim();
        }

        setIsSaving(true);
        setError('');
        setSuccessMessage('');

        const resp = await updateUserItem(token, userId, payload);
        if (!resp?.success) {
            setError(resp?.message || 'No se pudo actualizar el usuario.');
            setIsSaving(false);
            return;
        }

        setForm((previous) => ({
            ...previous,
            password: '',
        }));
        setSuccessMessage(resp?.message || 'Usuario actualizado correctamente.');
        setIsSaving(false);
    }, [token, userId, currentUserId, isSaving, form]);

    return {
        form,
        isLoading,
        isSaving,
        error,
        successMessage,
        handleInputChange,
        handleStatusChange,
        handleSubmit,
    };
};
